import {
	ReactElement,
	useCallback,
	useContext,
	useMemo,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { InteractionState, TileContext } from "../TileContext";
import {
	BusyIndicator,
	NoResults,
	PopoutableContent,
	Portal,
	renderReactDialog
} from "client/Components";
import styles from "./EmbedLiveTileContent.module.less";
import { hasStackingContextIssues } from "client_legacy/ndaw/mobile";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { isNotNil } from "client/Misc/utils";
import { AppType } from "client/Api/Apps/common";
import { AppTypeSpecificTileSettings } from "../../RenderState/common";
import { EmbedAppContent } from "client/Features/Embed/EmbedAppContent";
import { never } from "client_legacy/ndaw/errors";
import { ContentLoadingState, useEmbedContentLoading } from "client/Features/Embed/useEmbedContentLoading";
import { localize } from "client_legacy/ndaw/culture";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type EmbedAppTileSettings = AppTypeSpecificTileSettings<AppType.Embed>;

export type EmbedLiveTileContentProps = {
	settings: EmbedAppTileSettings;
	onNavigate: () => void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const EmbedLiveTileContent = (props: EmbedLiveTileContentProps): ReactElement => {
	const {
		settings,
		onNavigate,
		onOpenTileMenu
	} = props;

	const {
		hideHeader,
		hideFooter
	} = settings;

	const {
		interactionState,
		conditionalAccess,
		maintenanceWindow
	} = useContext(TileContext);

	const isUnavailable: boolean = isNotNil(maintenanceWindow)
		|| isNotNil(conditionalAccess)
		|| interactionState === InteractionState.Disabled;

	const [isPoppedOut, setIsPoppedOut] = useState<boolean>(false);

	const shouldUseSeparateDomForPopout: boolean = useMemo(
		() => hasStackingContextIssues(),
		[]
	);

	const {
		name,
		icon,
		environmentAppId,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const contentLoadingState: ContentLoadingState = useEmbedContentLoading(environmentAppId, !isUnavailable);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		conditionalAccess,
		maintenanceWindow,
		navigationHandler: onNavigate,
		appIsLicensed,
		appIsNotAccessible
	});

	const popOutHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			setIsPoppedOut(true);
		},
		[addAnalyticsEvent]
	);

	const defaultActions = useMemo(
		(): readonly Action<ActionContext>[] => listDefaultActions({
			enlarge: () => popOutHandler()
		}),
		[popOutHandler]
	);

	const actionContext = useMemo(
		(): ActionContext => ({
			isLoaded: contentLoadingState.kind === "loaded"
		}),
		[contentLoadingState.kind]
	);

	if (isNotNil(maintenanceWindow) || isNotNil(conditionalAccess)) {
		return (
			<LiveTileContent
				icon={icon}
				name={name}
				onOpenTileMenu={onOpenTileMenu}
				overlay={overlayState}
				overlayStatusIcon={<TileStatus />}
				isPoppedOut={isPoppedOut}
			>
			</LiveTileContent>
		);
	}

	return (
		<>
			<LiveTileContent
				icon={icon}
				name={name}
				overlay={overlayState}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
				isPoppedOut={isPoppedOut}
			>
				{!hideHeader && (
					<LiveTileContent.Header
						icon={icon}
						actions={defaultActions}
						actionContext={actionContext}
					/>
				)}

				<LiveTileContent.Content>
					<PopoutableContent
						isPoppedOut={!shouldUseSeparateDomForPopout && isPoppedOut}
						onPopin={() => setIsPoppedOut(false)}
						title={name}
					>
						{!isUnavailable && (
							<EmbedContent
								loadingState={contentLoadingState}
							/>
						)}
					</PopoutableContent>
				</LiveTileContent.Content>

				{!hideFooter && (
					<LiveTileContent.Footer
						name={name}
						onClick={onNavigate}
					/>
				)}
			</LiveTileContent>

			{shouldUseSeparateDomForPopout && isPoppedOut && (
				<Portal container={document.body}>
					<EmbedTilePopoutableContentManaged
						name={name}
						onClose={() => setIsPoppedOut(false)}
						loadingState={contentLoadingState}
					/>
				</Portal>
			)}
		</>
	);
};
EmbedLiveTileContent.displayName = "EmbedLiveTileContent";

type ActionContext = {
	isLoaded: boolean;
};

type ActionHandlers = {
	enlarge: () => void;
};

function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<ActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "enlarge",
			title: localize("_Enlarge"),
			icon: "arrow-up-right-from-square",
			canExecute: (x) => x.isLoaded,
			execute: () => handlers.enlarge()
		}
	];
}

type EmbedContentProps = {
	loadingState: ContentLoadingState;
};

const EmbedContent = (props: EmbedContentProps): ReactElement => {
	const {
		loadingState
	} = props;

	const renderContent = (): ReactElement => {
		switch (loadingState.kind) {
			case "not-started":
			case "pending":
				return <BusyIndicator />;
			case "loaded":
				const { kind, html } = loadingState.model;

				return (
					<EmbedAppContent
						kind={kind}
						html={html}
						trackingId="content"
					/>
				);
			case "error":
			case "forbidden":
				return (
					<NoResults
						icon="gear"
						title={localize("_ErrorLoadingTheData")}
						message={localize("_ErrorDuringDataRetrieval")}
						height="fill"
					/>
				);
			default:
				never(loadingState);
		}
	};


	return (
		<div className={styles.hostContainer}>
			{renderContent()}
		</div>
	);
};
EmbedContent.displayName = "EmbedContent";

type EmbedTilePopoutableContentManagedProps = {
	name: string;
	onClose: () => void;
	loadingState: ContentLoadingState;
};

const EmbedTilePopoutableContentManaged = (props: EmbedTilePopoutableContentManagedProps): ReactElement => {
	const {
		name,
		onClose,
		loadingState
	} = props;

	return (
		<PopoutableContent
			isPoppedOut
			onPopin={onClose}
			title={name}
		>
			<EmbedContent
				loadingState={loadingState}
			/>
		</PopoutableContent>
	);
};
EmbedTilePopoutableContentManaged.displayName = "EmbedTilePopoutableContentManaged";

export type EmbedTilePopoutableContentProps = {
	name: string;
	environmentAppId: number;
	onClose: () => void;
};

export const EmbedTilePopoutableContent = (props: EmbedTilePopoutableContentProps): ReactElement => {
	const {
		name,
		environmentAppId,
		onClose
	} = props;

	const loadingState: ContentLoadingState = useEmbedContentLoading(environmentAppId, true);

	return (
		<EmbedTilePopoutableContentManaged
			name={name}
			onClose={onClose}
			loadingState={loadingState}
		/>
	);
};
EmbedTilePopoutableContent.displayName = "EmbedTilePopoutableContent";

export function openEmbedTilePopoutableContent(name: string, environmentAppId: number): void {
	renderReactDialog((onClose) => (
		<EmbedTilePopoutableContent
			name={name}
			environmentAppId={environmentAppId}
			onClose={onClose}
		/>
	));
}